import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/apiClient';
import useAuth from '../hooks/useAuth';
import Modal from '../components/Modal';

const getMaxDobDate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 2);
  return d.toISOString().split('T')[0];
};

const getFullImageUrl = (img) => {
  if (!img) return null;
  const str = String(img).trim();
  if (!str || ['null', 'undefined', 'none', 'false', '0'].includes(str.toLowerCase())) return null;
  if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:')) return str;
  const clean = str.replace(/^\/+/, '').replace(/^uploads\//, '');
  return `https://pub-8403cb60474c4e039483cf5333503261.r2.dev/${clean}`;
};

export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const from = searchParams.get('from');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [branches, setBranches] = useState([]);
  const [branchClasses, setBranchClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const targetBranchId = editForm.branch_id || data?.student?.branch_id;
    if (isEditing && targetBranchId) {
      fetchBranchClasses(targetBranchId);
    } else {
      setBranchClasses([]);
    }
  }, [editForm.branch_id, data?.student?.branch_id, isEditing]);

  async function fetchBranchClasses(bId) {
    setClassesLoading(true);
    try {
      const res = await api.get(`/branches/${bId}/classes`);
      setBranchClasses((res.data.data || []).filter(c => c.is_active === 1));
    } catch (err) {
      setBranchClasses([]);
    } finally {
      setClassesLoading(false);
    }
  }
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactModal, setContactModal] = useState({ open: false, mode: 'add', data: {} });
  const [studentReports, setStudentReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [studentActivities, setStudentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [imgError, setImgError] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const [isEditingMedical, setIsEditingMedical] = useState(false);
  const [medicalForm, setMedicalForm] = useState({});
  const [savingMedical, setSavingMedical] = useState(false);

  const [allParentsList, setAllParentsList] = useState([]);
  const [isLinkParentOpen, setIsLinkParentOpen] = useState(false);
  const [linkParentForm, setLinkParentForm] = useState({ parent_id: '', relation: 'Father', is_primary: true });
  const [linkingParent, setLinkingParent] = useState(false);

  const [isAddParentOpen, setIsAddParentOpen] = useState(false);
  const [addParentForm, setAddParentForm] = useState({ name: '', phone: '', email: '', relation: 'Father', is_primary: true });
  const [countryCode, setCountryCode] = useState('+27');
  const [addingParent, setAddingParent] = useState(false);

  const [isEditParentOpen, setIsEditParentOpen] = useState(false);
  const [editParentForm, setEditParentForm] = useState({ id: '', name: '', phone: '', email: '', relation: 'Father', is_primary: false });
  const [editCountryCode, setEditCountryCode] = useState('+27');
  const [editingParentLoading, setEditingParentLoading] = useState(false);

  const [contactCountryCode, setContactCountryCode] = useState('+27');

  const [classHistory, setClassHistory] = useState([]);
  const [selectedHistoryClassId, setSelectedHistoryClassId] = useState('');
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [filteredPickupLogs, setFilteredPickupLogs] = useState([]);
  const [loadingPickup, setLoadingPickup] = useState(false);

  useEffect(() => {
    load();
    loadBranches();
    loadClassHistory();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchStudentReports();
    } else if (activeTab === 'activities') {
      fetchStudentActivities();
    } else if (activeTab === 'attendance') {
      fetchStudentAttendance();
    } else if (activeTab === 'pickup') {
      fetchStudentPickupLogs();
    }
  }, [activeTab, id, selectedHistoryClassId]);

  async function fetchStudentAttendance() {
    setLoadingAttendance(true);
    try {
      const selectedHistory = classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId));
      const activeYear = selectedHistory?.academic_year || new Date().getFullYear();

      const res = await api.get(`/students/${id}/attendance`, { params: { academic_year: activeYear } });
      setFilteredAttendance(res.data.data || []);
    } catch (err) {
      console.error('Error fetching filtered attendance:', err);
    } finally {
      setLoadingAttendance(false);
    }
  }

  async function fetchStudentPickupLogs() {
    setLoadingPickup(true);
    try {
      const selectedHistory = classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId));
      const activeYear = selectedHistory?.academic_year || new Date().getFullYear();

      const res = await api.get(`/pickup/student/${id}`, { params: { academic_year: activeYear } });
      setFilteredPickupLogs(res.data.data || []);
    } catch (err) {
      console.error('Error fetching filtered pickup logs:', err);
    } finally {
      setLoadingPickup(false);
    }
  }

  async function loadClassHistory() {
    try {
      const res = await api.get(`/students/${id}/class-history`);
      const history = res.data.data || [];
      setClassHistory(history);
      if (history.length > 0) {
        setSelectedHistoryClassId(String(history[0].class_id));
      }
    } catch (err) {
      setClassHistory([]);
    }
  }

  async function fetchStudentReports() {
    setLoadingReports(true);
    try {
      const selectedHistory = classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId));
      const activeYear = selectedHistory?.academic_year || new Date().getFullYear();

      const params = { student_id: id, academic_year: activeYear };
      const res = await api.get('/admin/student-reports', { params });
      setStudentReports(res.data.data || []);
    } catch (err) {
      // ignore
    } finally {
      setLoadingReports(false);
    }
  }

  async function fetchStudentActivities() {
    setLoadingActivities(true);
    try {
      const selectedHistory = classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId));
      const activeYear = selectedHistory?.academic_year || new Date().getFullYear();

      const params = { student_id: id, academic_year: activeYear };
      const res = await api.get('/activities', { params });
      setStudentActivities(res.data.data || []);
    } catch (err) {
      console.error('Error loading activities:', err);
    } finally {
      setLoadingActivities(false);
    }
  }

  const updateStatus = async (activityId, status) => {
    try {
      await api.put(`/activities/${activityId}/status`, { status });
      setSelectedActivity(prev => (prev ? { ...prev, status } : null));
      fetchStudentActivities();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update activity status');
    }
  };

  const startMedicalEdit = () => {
    const s = student || {};
    const m = s.medical || {};
    setMedicalForm({
      gp_name: s.gp_name || m.gp_name || '',
      passport_national_id: s.passport_national_id || m.passport_national_id || '',
      country_of_origin: s.country_of_origin || m.country_of_origin || '',
      race: s.race || m.race || '',
      medical_condition: s.medical_condition || m.medical_condition || '',
      medication: s.medication || m.medication || '',
      has_allergies: Boolean(s.has_allergies || m.has_allergies),
      allergies_details: s.allergies_details || m.allergies_details || '',
      doctor_details: s.doctor_details || m.doctor_details || ''
    });
    setIsEditingMedical(true);
  };

  const handleSaveMedical = async () => {
    setSavingMedical(true);
    try {
      await api.put(`/students/${id}/medical`, medicalForm);
      await load();
      setIsEditingMedical(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update medical info');
    } finally {
      setSavingMedical(false);
    }
  };

  const generateParentPassword = (name) => {
    if (!name) return 'Parent@1234';
    const cleanName = String(name).replace(/[^a-zA-Z]/g, '').toUpperCase();
    const namePart = cleanName.slice(0, 4).padEnd(4, 'X');
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${namePart}${day}${month}`;
  };

  const openLinkParentModal = async () => {
    try {
      const res = await api.get('/users', { params: { role: 'parent' } });
      setAllParentsList(res.data.data || []);
      setLinkParentForm({ parent_id: '', relation: 'Father', is_primary: true });
      setIsLinkParentOpen(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to load parents list');
    }
  };

  const handleLinkParentSubmit = async (e) => {
    e.preventDefault();
    if (!linkParentForm.parent_id) {
      alert('Please select a parent from the list');
      return;
    }
    setLinkingParent(true);
    try {
      await api.post(`/students/${id}/parents`, {
        parent_id: linkParentForm.parent_id,
        relation: linkParentForm.relation,
        is_primary: linkParentForm.is_primary ? 1 : 0
      });
      await load();
      setIsLinkParentOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to link parent');
    } finally {
      setLinkingParent(false);
    }
  };

  const openAddParentModal = () => {
    setAddParentForm({ name: '', phone: '', email: '', relation: 'Father', is_primary: true });
    setCountryCode('+27');
    setIsAddParentOpen(true);
  };

  const handleAddAndLinkParentSubmit = async (e) => {
    e.preventDefault();
    if (!addParentForm.name.trim()) {
      alert('Parent name is required');
      return;
    }

    let finalPhone = '';
    if (addParentForm.phone) {
      const cleanPhone = addParentForm.phone.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        alert('Please enter a valid 10-digit mobile phone number');
        return;
      }
      finalPhone = `${countryCode}${cleanPhone}`;
    }

    setAddingParent(true);
    try {
      const autoPassword = generateParentPassword(addParentForm.name);
      const createRes = await api.post('/users', {
        name: addParentForm.name.trim(),
        phone: finalPhone || undefined,
        email: addParentForm.email ? addParentForm.email.trim() : undefined,
        role: 'parent',
        password: autoPassword
      });

      const newParentId = createRes.data.data?.id;

      if (!newParentId) {
        throw new Error('Could not retrieve new parent account ID');
      }

      await api.post(`/students/${id}/parents`, {
        parent_id: newParentId,
        relation: addParentForm.relation,
        is_primary: addParentForm.is_primary ? 1 : 0
      });

      await load();
      setIsAddParentOpen(false);
      alert(`Parent "${addParentForm.name}" created and linked successfully!`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create and link parent');
    } finally {
      setAddingParent(false);
    }
  };

  const openEditParentModal = (p) => {
    let rawPhone = p.phone || '';
    let foundCode = '+27';
    let digitsOnly = rawPhone.replace(/\D/g, '');

    const countryOptions = ['+27', '+91', '+1', '+44', '+971', '+61', '+65'];
    for (const code of countryOptions) {
      const cCodeDigits = code.replace('+', '');
      if (rawPhone.startsWith(code) || digitsOnly.startsWith(cCodeDigits)) {
        foundCode = code;
        if (digitsOnly.startsWith(cCodeDigits)) {
          digitsOnly = digitsOnly.slice(cCodeDigits.length);
        }
        break;
      }
    }

    setEditCountryCode(foundCode);
    setEditParentForm({
      id: p.id,
      name: p.name || '',
      phone: digitsOnly.slice(0, 10),
      email: p.email || '',
      relation: p.relation || 'Parent',
      is_primary: Boolean(p.is_primary)
    });
    setIsEditParentOpen(true);
  };

  const handleSaveEditParent = async (e) => {
    e.preventDefault();
    if (!editParentForm.name.trim()) {
      alert('Parent name is required');
      return;
    }

    let finalPhone = '';
    if (editParentForm.phone) {
      const cleanPhone = editParentForm.phone.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        alert('Please enter a valid 10-digit mobile phone number');
        return;
      }
      finalPhone = `${editCountryCode}${cleanPhone}`;
    }

    setEditingParentLoading(true);
    try {
      await api.put(`/users/${editParentForm.id}`, {
        name: editParentForm.name.trim(),
        phone: finalPhone || undefined,
        email: editParentForm.email ? editParentForm.email.trim() : undefined
      });

      await api.post(`/students/${id}/parents`, {
        parent_id: editParentForm.id,
        relation: editParentForm.relation,
        is_primary: editParentForm.is_primary ? 1 : 0
      });

      await load();
      setIsEditParentOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update parent details');
    } finally {
      setEditingParentLoading(false);
    }
  };

  const handleDeleteParentUser = async (parentId, parentName) => {
    if (!window.confirm(`Are you sure you want to permanently delete parent "${parentName}"? This will delete the parent user account.`)) {
      return;
    }
    try {
      await api.delete(`/users/${parentId}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete parent account');
    }
  };

  async function load() {
    try {
      const res = await api.get(`/admin/students/${id}`);
      setData(res.data.data);
      const s = res.data.data.student;
      setEditForm({
        ...s,
        date_of_birth: s.date_of_birth
          ? s.date_of_birth.split('T')[0]
          : '',
        date_of_admission: s.date_of_admission
          ? s.date_of_admission.split('T')[0]
          : ''
      });
      setProfileImagePreview(getFullImageUrl(s.profile_image) || '');
      setProfileImageFile(null);
      setImgError(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load student');
    }
  }

  async function loadBranches() {
    try {
      const res = await api.get('/admin/branches');
      setBranches(res.data.data || []);
    } catch (err) {}
  }

  async function handleSave() {
    if (editForm.date_of_birth) {
      const maxDob = getMaxDobDate();
      if (editForm.date_of_birth > maxDob) {
        alert('Student must be at least 2 years old (Date of birth cannot be in future or less than 2 years old)');
        return;
      }
    }
    setSaving(true);
    try {
      const formData = new FormData();
      if (editForm.full_name) formData.append('full_name', editForm.full_name.trim());
      if (editForm.date_of_birth) formData.append('date_of_birth', editForm.date_of_birth);
      if (editForm.date_of_admission) formData.append('date_of_admission', editForm.date_of_admission);
      if (editForm.gender) formData.append('gender', editForm.gender);
      if (editForm.class_id) formData.append('class_id', editForm.class_id);
      if (editForm.class_grade) formData.append('class_grade', editForm.class_grade);
      if (editForm.branch_id) formData.append('branch_id', editForm.branch_id);
      if (editForm.blood_group) formData.append('blood_group', editForm.blood_group);
      if (editForm.height) formData.append('height', editForm.height);
      if (editForm.weight) formData.append('weight', editForm.weight);
      if (editForm.primary_language) formData.append('primary_language', editForm.primary_language);
      formData.append('is_active', editForm.is_active ? '1' : '0');
      if (profileImageFile) {
        formData.append('profile_image', profileImageFile);
      }

      await api.put(`/admin/students/${id}`, formData);
      await load();
      setIsEditing(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update student');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive() {
    try {
      await api.put(`/admin/students/${id}`, { is_active: data.student.is_active ? 0 : 1 });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update status');
    }
  }

  async function handleDelete() {
    try {
      await api.delete(`/admin/students/${id}`);
      navigate('/students');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete student');
    }
  }

  const openAddContactModal = () => {
    setContactCountryCode('+27');
    setContactModal({ open: true, mode: 'add', data: { name: '', relationship: '', phone: '' } });
  };

  const openEditContactModal = (c) => {
    let rawPhone = c.phone || '';
    let foundCode = '+27';
    let digitsOnly = rawPhone.replace(/\D/g, '');
    const countryOptions = ['+27', '+91', '+1', '+44', '+971', '+61', '+65'];
    for (const code of countryOptions) {
      const cCodeDigits = code.replace('+', '');
      if (rawPhone.startsWith(code) || digitsOnly.startsWith(cCodeDigits)) {
        foundCode = code;
        if (digitsOnly.startsWith(cCodeDigits)) {
          digitsOnly = digitsOnly.slice(cCodeDigits.length);
        }
        break;
      }
    }
    setContactCountryCode(foundCode);
    setContactModal({
      open: true,
      mode: 'edit',
      data: {
        id: c.id,
        name: c.name || '',
        relationship: c.relationship || '',
        phone: digitsOnly.slice(0, 10)
      }
    });
  };

  async function handleSaveContact() {
    try {
      const { mode, data: contactData } = contactModal;

      if (!contactData.name || !contactData.name.trim()) {
        alert('Please enter contact name');
        return;
      }

      let finalPhone = '';
      if (contactData.phone) {
        const cleanPhone = contactData.phone.replace(/\D/g, '');
        if (cleanPhone.length !== 10) {
          alert('Please enter a valid 10-digit mobile phone number');
          return;
        }
        finalPhone = `${contactCountryCode}${cleanPhone}`;
      } else {
        alert('Please enter a valid 10-digit mobile phone number');
        return;
      }

      const payload = {
        name: contactData.name.trim(),
        relationship: contactData.relationship ? contactData.relationship.trim() : null,
        phone: finalPhone
      };

      if (mode === 'add') {
        await api.post(`/admin/students/${id}/emergency-contacts`, payload);
      } else {
        await api.put(`/admin/emergency-contacts/${contactData.id}`, payload);
      }
      await load();
      setContactModal({ open: false, mode: 'add', data: {} });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save contact');
    }
  }

  async function handleDeleteContact(contactId) {
    try {
      await api.delete(`/admin/emergency-contacts/${contactId}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete contact');
    }
  }

  async function handleUnlinkParent(parentId, parentName) {
    if (!window.confirm(`Are you sure you want to unlink parent "${parentName}" from this student?`)) return;
    try {
      await api.delete(`/students/${id}/parents/${parentId}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to unlink parent');
    }
  }

  if (error) return <div className="error-message" style={{ margin: 24 }}>{error}</div>;
  if (!data) return <div style={{ padding: 32 }}>Loading...</div>;

  const { student, parents, allergies, conditions, medications, emergencyContacts, attendance, activities, pickupLogs } = data;
  const avatarUrl = getFullImageUrl(student.profile_image);
  const tabs = ['overview', 'medical', 'attendance', 'activities', 'pickup', 'reports'];

  return (
    <div style={{ padding: 24 }}>

      <button
        onClick={() => {
          if (from === 'parents') navigate('/parents');
          else if (from === 'attendance') navigate('/attendance');
          else navigate('/students');
        }}
        className="back-button"
      >
        {from === 'parents' ? '← Back to Parents' : from === 'attendance' ? '← Back to Attendance' : '← Back to Students'}
      </button>

      {/* Student Header */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#ffffff', fontSize: 28, fontWeight: 700, flexShrink: 0,
            overflow: 'hidden', border: '3px solid #e0e7ff',
            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.25)',
            textTransform: 'uppercase'
          }}>
            {avatarUrl && !imgError ? (
              <img
                src={avatarUrl}
                alt=""
                onError={() => setImgError(true)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              (student.full_name || 'S').charAt(0).toUpperCase()
            )}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 22 }}>{student.full_name}</h2>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
              {student.student_id} · {student.class_grade} · {student.branch_name}
            </p>

            {classHistory.length > 0 && (
              <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', background: '#eef2ff', padding: '3px 8px', borderRadius: 6, border: '1px solid #c7d2fe' }}>
                  🎓 Class History Filter:
                </span>
                <select
                  value={selectedHistoryClassId}
                  onChange={(e) => setSelectedHistoryClassId(e.target.value)}
                  style={{
                    padding: '4px 10px', borderRadius: 6, border: '1.5px solid #6366f1',
                    fontSize: 12, fontWeight: 600, color: '#1e293b', background: '#ffffff',
                    cursor: 'pointer', outline: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}
                >
                  {classHistory.map(h => (
                    <option key={`${h.class_id}-${h.academic_year}`} value={h.class_id}>
                      {h.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 13,
              background: student.is_active ? '#d1fae5' : '#fee2e2',
              color: student.is_active ? '#065f46' : '#991b1b'
            }}>
              {student.is_active ? 'Active' : 'Inactive'}
            </span>

            {/* Edit Button */}
            <button onClick={() => setIsEditing(true)} style={{
              padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
              background: '#2563eb', color: '#fff', border: 'none', fontWeight: 500
            }}>
              ✏️ Edit
            </button>

            {/* Active/Inactive Toggle */}
            <button onClick={handleToggleActive} style={{
              padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
              background: student.is_active ? '#fef3c7' : '#d1fae5',
              color: student.is_active ? '#92400e' : '#065f46',
              border: '1px solid #e5e7eb', fontWeight: 500
            }}>
              {student.is_active ? '⏸ Deactivate' : '▶ Activate'}
            </button>

            {/* Delete Button */}
            <button onClick={() => setShowDeleteConfirm(true)} style={{
              padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
              background: '#fee2e2', color: '#991b1b', border: 'none', fontWeight: 500
            }}>
              🗑 Delete
            </button>
          </div>
        </div>
      </div>

      {/* ── EDIT MODAL ── */}
      {isEditing && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 28,
            width: 560, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h3 style={{ marginTop: 0 }}>Edit Student — {student.full_name}</h3>

            {/* Profile Photo Upload */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, background: '#f8fafc', padding: 12, borderRadius: 10, border: '1px solid #e2e8f0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', background: '#e0e7ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', border: '2px solid #6366f1', flexShrink: 0,
                fontSize: 20, fontWeight: 700, color: '#3730a3'
              }}>
                {profileImagePreview ? (
                  <img src={profileImagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  (student.full_name || 'S').charAt(0).toUpperCase()
                )}
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                  Profile Photo (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setProfileImageFile(file);
                      setProfileImagePreview(URL.createObjectURL(file));
                    }
                  }}
                  style={{ fontSize: 13, color: '#475569' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                ['Full Name', 'full_name', 'text'],
                ['Date of Birth', 'date_of_birth', 'date'],
                ['Height', 'height', 'text'],
                ['Weight', 'weight', 'text'],
                ['Primary Language', 'primary_language', 'text'],
              ].map(([label, field, type]) => (
                <div key={field}>
                  <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>{label}</label>
                  <input
                    type={type}
                    max={type === 'date' ? getMaxDobDate() : undefined}
                    value={editForm[field] || ''}
                    onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 7,
                      border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>
              ))}

              {/* Class Dropdown */}
              <div>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Class</label>
                {classesLoading ? (
                  <div style={{ fontSize: 13, color: '#64748b', padding: '8px 0' }}>Loading branch classes...</div>
                ) : branchClasses.length === 0 ? (
                  <div style={{
                    padding: '6px 10px', background: '#fffbebf0', border: '1px solid #fde68a',
                    borderRadius: 7, color: '#b45309', fontSize: 12, fontWeight: 500
                  }}>
                    ⚠️ No active classes in this branch.
                  </div>
                ) : (
                  <select
                    value={(() => {
                      if (editForm.class_id) return String(editForm.class_id);
                      if (editForm.class_grade && branchClasses.length > 0) {
                        const matched = branchClasses.find(c => {
                          const fullName = c.section ? `${c.name} (${c.section})` : c.name;
                          return fullName.trim().toLowerCase() === String(editForm.class_grade).trim().toLowerCase() ||
                                 c.name.trim().toLowerCase() === String(editForm.class_grade).trim().toLowerCase();
                        });
                        if (matched) return String(matched.id);
                      }
                      return '';
                    })()}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedClassObj = branchClasses.find(c => String(c.id) === String(selectedId));
                      const formattedClassName = selectedClassObj
                        ? (selectedClassObj.section ? `${selectedClassObj.name} (${selectedClassObj.section})` : selectedClassObj.name)
                        : '';
                      setEditForm(p => ({
                        ...p,
                        class_id: selectedId,
                        class_grade: formattedClassName
                      }));
                    }}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 7,
                      border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', background: '#fff', cursor: 'pointer'
                    }}
                  >
                    <option value="">Select Class</option>
                    {branchClasses.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.section ? `(${c.section})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Blood Group */}
              <div>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Blood Group</label>
                <select
                  value={editForm.blood_group || ''}
                  onChange={e => setEditForm(p => ({ ...p, blood_group: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 7,
                    border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>

              {/* Gender */}
              <div>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Gender</label>
                <select
                  value={editForm.gender || ''}
                  onChange={e => setEditForm(p => ({ ...p, gender: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #e5e7eb', fontSize: 14 }}
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Branch */}
              <div>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Branch</label>
                {user?.role === 'admin' ? (
                  <select
                    value={editForm.branch_id || ''}
                    onChange={e => setEditForm(p => ({ ...p, branch_id: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #e5e7eb', fontSize: 14 }}
                  >
                    <option value="">Select branch</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                ) : (
                  <div style={{
                    padding: '8px 10px',
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '7px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#334155'
                  }}>
                    📍 {student.branch_name || 'Current Branch'}
                  </div>
                )}
              </div>
            </div>

            {error && <div style={{ color: '#991b1b', marginTop: 12, fontSize: 13 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => { setIsEditing(false); setError(''); }} style={{
                padding: '8px 20px', borderRadius: 8, border: '1px solid #e5e7eb',
                background: '#fff', cursor: 'pointer', fontSize: 14
              }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} style={{
                padding: '8px 20px', borderRadius: 8, border: 'none',
                background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600
              }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 28,
            width: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h3 style={{ marginTop: 0, color: '#991b1b' }}>Delete Student?</h3>
            <p style={{ color: '#6b7280', fontSize: 14 }}>
              Are you sure you want to delete <strong>{student.full_name}</strong>?
              This action <strong>cannot be undone</strong>.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{
                padding: '8px 20px', borderRadius: 8, border: '1px solid #e5e7eb',
                background: '#fff', cursor: 'pointer', fontSize: 14
              }}>
                Cancel
              </button>
              <button onClick={handleDelete} style={{
                padding: '8px 20px', borderRadius: 8, border: 'none',
                background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600
              }}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Add/Edit Modal */}
      {contactModal.open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 28,
            width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h3 style={{ marginTop: 0, color: '#1e1b4b' }}>
              {contactModal.mode === 'add' ? '➕ Add Emergency Contact' : '✏️ Edit Emergency Contact'}
            </h3>

            <div style={{ display: 'grid', gap: 14, marginTop: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Contact Name *</label>
                <input
                  type="text"
                  value={contactModal.data.name || ''}
                  onChange={e => setContactModal(p => ({ ...p, data: { ...p.data, name: e.target.value } }))}
                  placeholder="Full name"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  required
                />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Relationship</label>
                <input
                  type="text"
                  value={contactModal.data.relationship || ''}
                  onChange={e => setContactModal(p => ({ ...p, data: { ...p.data, relationship: e.target.value } }))}
                  placeholder="e.g. Father, Mother, Uncle"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Phone Number (10 digits) *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={contactCountryCode}
                    onChange={(e) => setContactCountryCode(e.target.value)}
                    style={{ width: '90px', padding: '9px 6px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  >
                    <option value="+27">🇿🇦 +27</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+971">🇦🇪 +971</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+65">🇸🇬 +65</option>
                  </select>
                  <input
                    type="tel"
                    value={contactModal.data.phone || ''}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setContactModal(p => ({ ...p, data: { ...p.data, phone: val } }));
                    }}
                    placeholder="10-digit mobile number"
                    style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                    required
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setContactModal({ open: false, mode: 'add', data: {} })}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveContact}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
              >
                {contactModal.mode === 'add' ? 'Add Contact' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Existing Parent Modal */}
      {isLinkParentOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 28,
            width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h3 style={{ marginTop: 0, color: '#1e1b4b' }}>🔗 Link Existing Parent</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: -6, marginBottom: 16 }}>
              Select a registered parent account to link with <strong>{student.full_name}</strong>
            </p>

            <form onSubmit={handleLinkParentSubmit} style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Select Parent</label>
                <select
                  value={linkParentForm.parent_id}
                  onChange={(e) => setLinkParentForm({ ...linkParentForm, parent_id: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  required
                >
                  <option value="">-- Choose a Parent --</option>
                  {allParentsList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.phone || p.email || 'No Contact'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Relationship</label>
                <select
                  value={linkParentForm.relation}
                  onChange={(e) => setLinkParentForm({ ...linkParentForm, relation: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                >
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Parent">Parent</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <input
                  type="checkbox"
                  id="link_is_primary"
                  checked={linkParentForm.is_primary}
                  onChange={(e) => setLinkParentForm({ ...linkParentForm, is_primary: e.target.checked })}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <label htmlFor="link_is_primary" style={{ fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                  Mark as Primary Guardian ⭐
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setIsLinkParentOpen(false)}
                  style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={linkingParent}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                >
                  {linkingParent ? 'Linking...' : 'Link Parent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add New Parent & Auto Link Modal */}
      {isAddParentOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 28,
            width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h3 style={{ marginTop: 0, color: '#1e1b4b' }}>➕ Add New Parent & Link</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: -6, marginBottom: 16 }}>
              Create a new parent account and automatically link to <strong>{student.full_name}</strong>
            </p>

            <form onSubmit={handleAddAndLinkParentSubmit} style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Parent Full Name *</label>
                <input
                  type="text"
                  value={addParentForm.name}
                  onChange={(e) => setAddParentForm({ ...addParentForm, name: e.target.value })}
                  placeholder="Enter parent full name"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  required
                />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Phone Number (10 digits)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    style={{ width: '90px', padding: '9px 6px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  >
                    <option value="+27">🇿🇦 +27</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+971">🇦🇪 +971</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+65">🇸🇬 +65</option>
                  </select>
                  <input
                    type="tel"
                    value={addParentForm.phone}
                    onChange={(e) => setAddParentForm({ ...addParentForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="10-digit mobile number"
                    style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Email Address</label>
                <input
                  type="email"
                  value={addParentForm.email}
                  onChange={(e) => setAddParentForm({ ...addParentForm, email: e.target.value })}
                  placeholder="parent@example.com (optional)"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Relationship</label>
                <select
                  value={addParentForm.relation}
                  onChange={(e) => setAddParentForm({ ...addParentForm, relation: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                >
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Parent">Parent</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <input
                  type="checkbox"
                  id="add_is_primary"
                  checked={addParentForm.is_primary}
                  onChange={(e) => setAddParentForm({ ...addParentForm, is_primary: e.target.checked })}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <label htmlFor="add_is_primary" style={{ fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                  Mark as Primary Guardian ⭐
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setIsAddParentOpen(false)}
                  style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingParent}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                >
                  {addingParent ? 'Saving...' : 'Save & Link Parent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Parent Modal */}
      {isEditParentOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 28,
            width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h3 style={{ marginTop: 0, color: '#1e1b4b' }}>✏️ Edit Parent Details</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: -6, marginBottom: 16 }}>
              Update contact info and relationship for <strong>{editParentForm.name}</strong>
            </p>

            <form onSubmit={handleSaveEditParent} style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Parent Full Name *</label>
                <input
                  type="text"
                  value={editParentForm.name}
                  onChange={(e) => setEditParentForm({ ...editParentForm, name: e.target.value })}
                  placeholder="Enter parent full name"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  required
                />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Phone Number (10 digits)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={editCountryCode}
                    onChange={(e) => setEditCountryCode(e.target.value)}
                    style={{ width: '90px', padding: '9px 6px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  >
                    <option value="+27">🇿🇦 +27</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+971">🇦🇪 +971</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+65">🇸🇬 +65</option>
                  </select>
                  <input
                    type="tel"
                    value={editParentForm.phone}
                    onChange={(e) => setEditParentForm({ ...editParentForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="10-digit mobile number"
                    style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Email Address</label>
                <input
                  type="email"
                  value={editParentForm.email}
                  onChange={(e) => setEditParentForm({ ...editParentForm, email: e.target.value })}
                  placeholder="parent@example.com (optional)"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Relationship</label>
                <select
                  value={editParentForm.relation}
                  onChange={(e) => setEditParentForm({ ...editParentForm, relation: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                >
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Parent">Parent</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <input
                  type="checkbox"
                  id="edit_is_primary"
                  checked={editParentForm.is_primary}
                  onChange={(e) => setEditParentForm({ ...editParentForm, is_primary: e.target.checked })}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <label htmlFor="edit_is_primary" style={{ fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                  Mark as Primary Guardian ⭐
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setIsEditParentOpen(false)}
                  style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editingParentLoading}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                >
                  {editingParentLoading ? 'Saving...' : 'Save Parent Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
            border: activeTab === tab ? 'none' : '1px solid #e5e7eb',
            background: activeTab === tab ? '#2563eb' : '#fff',
            color: activeTab === tab ? '#fff' : '#374151',
            fontWeight: activeTab === tab ? 600 : 400,
            textTransform: 'capitalize'
          }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          {/* Basic Info */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
            <h3 style={{ marginTop: 0, fontSize: 16 }}>Basic Information</h3>
            {[
              ['Date of Birth', student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'],
              ['Date of Admission', student.date_of_admission ? new Date(student.date_of_admission).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'],
              ['Gender', student.gender],
              ['Blood Group', student.blood_group],
              ['Height', student.height],
              ['Weight', student.weight],
              ['Language', student.primary_language],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ color: '#6b7280', fontSize: 14 }}>{label}</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{value || '-'}</span>
              </div>
            ))}
          </div>

          {/* Parents */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Parents / Guardians</h3>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={openLinkParentModal}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    border: '1px solid #93c5fd',
                    background: '#eff6ff',
                    color: '#1d4ed8',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                  title="Link an existing parent from database"
                >
                  🔗 Link Parent
                </button>
                <button
                  type="button"
                  onClick={openAddParentModal}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#2563eb',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                  title="Create a new parent and link to this student"
                >
                  ➕ Add Parent
                </button>
              </div>
            </div>

            {parents.length ? (
              parents.map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name} {p.is_primary ? '⭐' : ''}</div>
                    <div style={{ color: '#6b7280', fontSize: 13 }}>{p.relation} · {p.phone}</div>
                    <div style={{ color: '#6b7280', fontSize: 13 }}>{p.email}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button
                      onClick={() => openEditParentModal(p)}
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1',
                        background: '#fff', color: '#1e293b', cursor: 'pointer', fontSize: 12, fontWeight: 500
                      }}
                      title="Edit parent details"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleUnlinkParent(p.id, p.name)}
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: '1px solid #fed7aa',
                        background: '#fff7ed', color: '#c2410c', cursor: 'pointer', fontSize: 12, fontWeight: 500
                      }}
                      title="Unlink parent from this student"
                    >
                      🔗 Unlink
                    </button>
                    <button
                      onClick={() => handleDeleteParentUser(p.id, p.name)}
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: 'none',
                        background: '#fee2e2', color: '#991b1b', cursor: 'pointer', fontSize: 12, fontWeight: 500
                      }}
                      title="Permanently delete parent user account"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 12px', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1' }}>
                <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 12px 0' }}>No parents linked to this student yet.</p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
                  <button
                    type="button"
                    onClick={openLinkParentModal}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: '1px solid #93c5fd',
                      background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', fontSize: 13, fontWeight: 600
                    }}
                  >
                    🔗 Link Existing Parent
                  </button>
                  <button
                    type="button"
                    onClick={openAddParentModal}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: 'none',
                      background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600
                    }}
                  >
                    ➕ Add New Parent
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Emergency Contacts */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb', gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Emergency Contacts</h3>
              <button
                onClick={openAddContactModal}
                style={{
                  padding: '6px 14px', borderRadius: 7, border: 'none',
                  background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500
                }}
              >
                + Add Contact
              </button>
            </div>
            {emergencyContacts.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Relation</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Phone</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emergencyContacts.map((c, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px' }}>{c.name}</td>
                      <td style={{ padding: '8px 12px' }}>{c.relationship}</td>
                      <td style={{ padding: '8px 12px' }}>{c.phone}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => openEditContactModal(c)}
                            style={{
                              padding: '4px 10px', borderRadius: 6, border: '1px solid #e5e7eb',
                              background: '#fff', cursor: 'pointer', fontSize: 12
                            }}
                          >✏️ Edit</button>
                          <button
                            onClick={() => handleDeleteContact(c.id)}
                            style={{
                              padding: '4px 10px', borderRadius: 6, border: 'none',
                              background: '#fee2e2', color: '#991b1b', cursor: 'pointer', fontSize: 12
                            }}
                          >🗑 Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p style={{ color: '#9ca3af' }}>No emergency contacts</p>}
          </div>

        </div>
      )}

      {/* Medical Tab */}
      {activeTab === 'medical' && (
        <div style={{ display: 'grid', gap: 16 }}>
          {/* General Medical & Health Info Card */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, borderBottom: '1px solid #f3f4f6', paddingBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>🩺</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: 17, color: '#1e1b4b' }}>Medical Profile & Health Info</h3>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Student medical records, allergies, doctor & national identity details</div>
                </div>
              </div>
              {!isEditingMedical && (
                <button
                  onClick={startMedicalEdit}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: '1px solid #cbd5e1',
                    background: '#f8fafc', color: '#1e293b', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 6
                  }}
                >
                  ✏️ Edit Medical Info
                </button>
              )}
            </div>

            {isEditingMedical ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>GP Name</label>
                  <input
                    type="text"
                    value={medicalForm.gp_name || ''}
                    onChange={(e) => setMedicalForm({ ...medicalForm, gp_name: e.target.value })}
                    placeholder="General Practitioner / Clinic Name"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Doctor Details</label>
                  <input
                    type="text"
                    value={medicalForm.doctor_details || ''}
                    onChange={(e) => setMedicalForm({ ...medicalForm, doctor_details: e.target.value })}
                    placeholder="Doctor Name & Contact Info"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Passport / National ID</label>
                  <input
                    type="text"
                    value={medicalForm.passport_national_id || ''}
                    onChange={(e) => setMedicalForm({ ...medicalForm, passport_national_id: e.target.value })}
                    placeholder="Passport or National Identity No."
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Country of Origin</label>
                  <input
                    type="text"
                    value={medicalForm.country_of_origin || ''}
                    onChange={(e) => setMedicalForm({ ...medicalForm, country_of_origin: e.target.value })}
                    placeholder="Country of Origin"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Race / Ethnicity</label>
                  <input
                    type="text"
                    value={medicalForm.race || ''}
                    onChange={(e) => setMedicalForm({ ...medicalForm, race: e.target.value })}
                    placeholder="Race / Ethnicity"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Has Allergies?</label>
                  <select
                    value={medicalForm.has_allergies ? '1' : '0'}
                    onChange={(e) => setMedicalForm({ ...medicalForm, has_allergies: e.target.value === '1' })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  >
                    <option value="0">No Known Allergies</option>
                    <option value="1">Yes (Has Allergies)</option>
                  </select>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Allergies Details</label>
                  <textarea
                    rows={2}
                    value={medicalForm.allergies_details || ''}
                    onChange={(e) => setMedicalForm({ ...medicalForm, allergies_details: e.target.value })}
                    placeholder="Specify allergies (food, dust, medication, etc.)"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Medical Condition Summary</label>
                  <textarea
                    rows={2}
                    value={medicalForm.medical_condition || ''}
                    onChange={(e) => setMedicalForm({ ...medicalForm, medical_condition: e.target.value })}
                    placeholder="Medical conditions or special care notes..."
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Medication Notes</label>
                  <textarea
                    rows={2}
                    value={medicalForm.medication || ''}
                    onChange={(e) => setMedicalForm({ ...medicalForm, medication: e.target.value })}
                    placeholder="Prescribed medications, instructions or dosage..."
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => setIsEditingMedical(false)}
                    style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveMedical}
                    disabled={savingMedical}
                    style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                  >
                    {savingMedical ? 'Saving...' : 'Save Medical Info'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>GP / Clinic Name</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
                    {student.gp_name || student.medical?.gp_name || '—'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Doctor Details</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
                    {student.doctor_details || student.medical?.doctor_details || '—'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Passport / National ID</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
                    {student.passport_national_id || student.medical?.passport_national_id || '—'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Country of Origin</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
                    {student.country_of_origin || student.medical?.country_of_origin || '—'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Race / Ethnicity</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
                    {student.race || student.medical?.race || '—'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Allergies Status</div>
                  <div style={{ marginTop: 2 }}>
                    {(student.has_allergies || student.medical?.has_allergies) ? (
                      <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#fee2e2', color: '#991b1b' }}>
                        ⚠️ Has Allergies
                      </span>
                    ) : (
                      <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#d1fae5', color: '#065f46' }}>
                        ✅ No Known Allergies
                      </span>
                    )}
                  </div>
                </div>

                {(student.allergies_details || student.medical?.allergies_details) && (
                  <div style={{ gridColumn: '1 / -1', background: '#fef2f2', padding: 12, borderRadius: 8, border: '1px solid #fecaca' }}>
                    <div style={{ fontSize: 12, color: '#991b1b', fontWeight: 700 }}>⚠️ Allergies Details</div>
                    <div style={{ fontSize: 13, color: '#7f1d1d', marginTop: 3 }}>
                      {student.allergies_details || student.medical?.allergies_details}
                    </div>
                  </div>
                )}

                {(student.medical_condition || student.medical?.medical_condition) && (
                  <div style={{ gridColumn: '1 / -1', background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>📋 Medical Condition Summary</div>
                    <div style={{ fontSize: 13, color: '#1e293b', marginTop: 3 }}>
                      {student.medical_condition || student.medical?.medical_condition}
                    </div>
                  </div>
                )}

                {(student.medication || student.medical?.medication) && (
                  <div style={{ gridColumn: '1 / -1', background: '#eff6ff', padding: 12, borderRadius: 8, border: '1px solid #bfdbfe' }}>
                    <div style={{ fontSize: 12, color: '#1e40af', fontWeight: 700 }}>💊 Medication Notes</div>
                    <div style={{ fontSize: 13, color: '#1e3a8a', marginTop: 3 }}>
                      {student.medication || student.medical?.medication}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Allergies List Card */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
            <h3 style={{ marginTop: 0, fontSize: 16 }}>Allergies List</h3>
            {allergies && allergies.length ? allergies.map((a, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{a.name}</span>
                <span style={{
                  padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                  background: a.severity === 'severe' ? '#fee2e2' : a.severity === 'moderate' ? '#fef3c7' : '#d1fae5',
                  color: a.severity === 'severe' ? '#991b1b' : a.severity === 'moderate' ? '#92400e' : '#065f46'
                }}>{a.severity}</span>
              </div>
            )) : <p style={{ color: '#9ca3af' }}>No detailed allergies registered</p>}
          </div>

          {/* Medical Conditions List Card */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
            <h3 style={{ marginTop: 0, fontSize: 16 }}>Registered Medical Conditions</h3>
            {conditions && conditions.length ? conditions.map((c, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{c.condition_name}</div>
                <div style={{ color: '#6b7280', fontSize: 13 }}>{c.description}</div>
              </div>
            )) : <p style={{ color: '#9ca3af' }}>No detailed conditions registered</p>}
          </div>

          {/* Medications List Card */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
            <h3 style={{ marginTop: 0, fontSize: 16 }}>Registered Medications</h3>
            {medications && medications.length ? medications.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{m.name}</span>
                <span style={{ fontSize: 13, color: '#6b7280' }}>{m.dosage}</span>
              </div>
            )) : <p style={{ color: '#9ca3af' }}>No detailed medications registered</p>}
          </div>
        </div>
      )}

      {/* Attendance Tab */}
      {activeTab === 'attendance' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
          <h3 style={{ marginTop: 0, fontSize: 16 }}>
            Attendance History ({classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId))?.label || 'Current Session'})
          </h3>
          {loadingAttendance ? (
            <div style={{ padding: 16, color: '#64748b' }}>Loading attendance records...</div>
          ) : (() => {
            const selectedHistory = classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId));
            const activeYear = selectedHistory?.academic_year || new Date().getFullYear();

            // Filter attendance records by selected academic_year
            const listToRender = filteredAttendance.length > 0 
              ? filteredAttendance 
              : (attendance || []).filter(a => {
                  const attDate = a.attendance_date ? String(a.attendance_date).split('T')[0] : '';
                  return attDate.startsWith(String(activeYear));
                });

            return listToRender.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {listToRender.map((a, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px' }}>{a.attendance_date ? a.attendance_date.split('T')[0] : '-'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          padding: '2px 10px', borderRadius: 12, fontSize: 12,
                          background: a.status === 'present' ? '#d1fae5' : a.status === 'late' ? '#fef3c7' : '#fee2e2',
                          color: a.status === 'present' ? '#065f46' : a.status === 'late' ? '#92400e' : '#991b1b'
                        }}>{a.status}</span>
                      </td>
                      <td style={{ padding: '8px 12px', color: '#6b7280' }}>{a.note || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p style={{ color: '#9ca3af', padding: '10px 0' }}>No attendance records found for this academic session ({activeYear}).</p>;
          })()}
        </div>
      )}

      {/* Activities Tab */}
      {activeTab === 'activities' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: '#1e293b' }}>
            Activities History ({classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId))?.label || 'Current Session'})
          </h3>
          {loadingActivities ? (
            <div style={{ padding: 16, color: '#64748b' }}>Loading activities...</div>
          ) : (() => {
            const selectedHistory = classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId));
            const activeYear = selectedHistory?.academic_year || new Date().getFullYear();

            const listToRender = (studentActivities || []).filter(a => {
              const actDate = a.activity_date ? String(a.activity_date).split('T')[0] : '';
              return actDate.startsWith(String(activeYear));
            });

            return listToRender.length ? (
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                      <th style={{ padding: '10px 14px', fontWeight: 600 }}>Activity</th>
                      <th style={{ padding: '10px 14px', fontWeight: 600 }}>Category</th>
                      <th style={{ padding: '10px 14px', fontWeight: 600 }}>Teacher</th>
                      <th style={{ padding: '10px 14px', fontWeight: 600 }}>Date & Time</th>
                      <th style={{ padding: '10px 14px', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '10px 14px', fontWeight: 600, textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listToRender.map((a, i) => {
                      const formattedDate = a.activity_date ? String(a.activity_date).split('T')[0] : '-';
                      return (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{a.title}</div>
                          {a.description && (
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {a.description}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                            background: '#ede9fe', color: '#7c3aed'
                          }}>{a.category}</span>
                        </td>
                        <td style={{ padding: '12px 14px', color: '#475569', fontWeight: 500 }}>
                          👩‍🏫 {a.teacher_name || 'Staff Teacher'}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#475569' }}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>📅 {formattedDate}</div>
                          {a.activity_time && <div style={{ fontSize: 11, color: '#64748b' }}>⏰ {a.activity_time}</div>}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                            background: a.status === 'approved' ? '#d1fae5' : a.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                            color: a.status === 'approved' ? '#065f46' : a.status === 'rejected' ? '#991b1b' : '#92400e'
                          }}>{a.status || 'pending'}</span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => setSelectedActivity(a)}
                            style={{
                              padding: '6px 14px',
                              borderRadius: 6,
                              border: '1px solid #93c5fd',
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Detail
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p style={{ color: '#9ca3af', padding: '10px 0' }}>No activities recorded for this academic session ({activeYear}).</p>;
        })()}
        </div>
      )}

      {/* Pickup Tab */}
      {activeTab === 'pickup' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
          <h3 style={{ marginTop: 0, fontSize: 16 }}>
            Pickup Logs History ({classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId))?.label || 'Current Session'})
          </h3>
          {loadingPickup ? (
            <div style={{ padding: 16, color: '#64748b' }}>Loading pickup logs...</div>
          ) : (() => {
            const selectedHistory = classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId));
            const activeYear = selectedHistory?.academic_year || new Date().getFullYear();

            const listToRender = (filteredPickupLogs || []).filter(p => {
              const pDate = p.pickup_date ? String(p.pickup_date).split('T')[0] : '';
              return pDate.startsWith(String(activeYear));
            });

            return listToRender.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Check-in</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Checkout</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Picked By</th>
                  </tr>
                </thead>
                <tbody>
                  {listToRender.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px' }}>{p.pickup_date ? p.pickup_date.split('T')[0] : '-'}</td>
                      <td style={{ padding: '8px 12px' }}>{p.checkin_time || '-'}</td>
                      <td style={{ padding: '8px 12px' }}>{p.checkout_time || '-'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <select
                          value={p.status || 'waiting'}
                          onChange={async (e) => {
                            try {
                              await api.put(`/admin/pickup/${p.id}`, { status: e.target.value });
                              await load();
                            } catch (err) {
                              setError(err.response?.data?.message || 'Unable to update pickup status');
                            }
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            background: p.status === 'picked' ? '#d1fae5' : p.status === 'waiting' ? '#fef3c7' : '#f3f4f6',
                            color: p.status === 'picked' ? '#065f46' : p.status === 'waiting' ? '#92400e' : '#374151'
                          }}
                        >
                          <option value="waiting">waiting</option>
                          <option value="picked">picked</option>
                          <option value="checked_in">checked_in</option>
                          <option value="cancelled">cancelled</option>
                        </select>
                      </td>
                      <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                        {p.picked_by_name || p.picked_by || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p style={{ color: '#9ca3af', padding: '10px 0' }}>No pickup logs found for this academic session ({activeYear}).</p>;
          })()}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
          <h3 style={{ marginTop: 0, fontSize: 16 }}>
            Student Reports ({classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId))?.label || 'Current Session'})
          </h3>
          {loadingReports ? (
            <div style={{ padding: 16, color: '#64748b' }}>Loading reports...</div>
          ) : (() => {
            const selectedHistory = classHistory.find(h => String(h.class_id) === String(selectedHistoryClassId));
            const activeYear = selectedHistory?.academic_year || new Date().getFullYear();

            const listToRender = (studentReports || []).filter(r => {
              const rYear = r.academic_year ? String(r.academic_year) : '';
              return rYear === String(activeYear) || rYear.startsWith(String(activeYear));
            });

            return listToRender.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Report Type</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Term</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Academic Year</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listToRender.map((report) => (
                    <tr key={report.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{report.report_type}</td>
                      <td style={{ padding: '8px 12px' }}>{report.term}</td>
                      <td style={{ padding: '8px 12px' }}>{report.academic_year}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: report.status === 'published' || report.is_published ? '#d1fae5' : '#fef3c7',
                          color: report.status === 'published' || report.is_published ? '#065f46' : '#92400e'
                        }}>
                          {report.status ? String(report.status).toUpperCase() : (report.is_published ? 'PUBLISHED' : 'DRAFT')}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <button
                          onClick={() => navigate(`/reports/${report.id}?from=student`)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            background: '#2563eb',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600
                          }}
                        >
                          View Report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p style={{ color: '#9ca3af', padding: '10px 0' }}>No reports found for this academic session ({activeYear}).</p>;
          })()}
        </div>
      )}

      {/* Activity Details Modal */}
      {selectedActivity && (
        <Modal isOpen={!!selectedActivity} onClose={() => setSelectedActivity(null)} title={`Activity Details #${selectedActivity.id || selectedActivity.activity_id || ''}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{
              background: '#faf5ff',
              padding: 16,
              borderRadius: 12,
              border: '1px solid #e9d5ff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, color: '#3b0764' }}>{selectedActivity.title}</h3>
                <div style={{ fontSize: 13, color: '#6b21a8', marginTop: 2, fontWeight: 600 }}>
                  Category: {selectedActivity.category}
                </div>
              </div>
              {selectedActivity.status && (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'capitalize',
                  background: selectedActivity.status === 'approved' ? '#d1fae5' : selectedActivity.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                  color: selectedActivity.status === 'approved' ? '#065f46' : selectedActivity.status === 'rejected' ? '#991b1b' : '#92400e'
                }}>
                  {selectedActivity.status}
                </span>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              background: '#f8fafc',
              padding: 12,
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              fontSize: 13
            }}>
              <div>
                <span style={{ color: '#64748b', fontSize: 11, display: 'block', fontWeight: 600 }}>Teacher</span>
                <strong style={{ color: '#1e293b' }}>{selectedActivity.teacher_name || student?.primary_teacher_name || 'Staff Teacher'}</strong>
              </div>
              <div>
                <span style={{ color: '#64748b', fontSize: 11, display: 'block', fontWeight: 600 }}>Branch</span>
                <strong style={{ color: '#1e293b' }}>{selectedActivity.branch_name || student?.branch_name || 'Branch 1'}</strong>
              </div>
              <div>
                <span style={{ color: '#64748b', fontSize: 11, display: 'block', fontWeight: 600 }}>Date</span>
                <strong style={{ color: '#1e293b' }}>{selectedActivity.activity_date ? String(selectedActivity.activity_date).split('T')[0] : '-'}</strong>
              </div>
              {selectedActivity.activity_time && (
                <div>
                  <span style={{ color: '#64748b', fontSize: 11, display: 'block', fontWeight: 600 }}>Time</span>
                  <strong style={{ color: '#1e293b' }}>{selectedActivity.activity_time}</strong>
                </div>
              )}
            </div>

            <div>
              <h4 style={{ margin: '0 0 6px', fontSize: 14, color: '#334155' }}>Description</h4>
              <div style={{
                background: '#f8fafc',
                padding: '12px 14px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                fontSize: 13,
                color: '#334155',
                lineHeight: 1.5
              }}>
                {selectedActivity.description || <span style={{ color: '#94a3b8' }}>No description provided.</span>}
              </div>
            </div>

            {selectedActivity.students && selectedActivity.students.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 6px', fontSize: 14, color: '#334155' }}>Tagged Students</h4>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {selectedActivity.students.map(st => (
                    <span key={st.id} style={{
                      padding: '4px 10px',
                      background: '#eff6ff',
                      color: '#1d4ed8',
                      borderRadius: '16px',
                      fontSize: 13,
                      fontWeight: 500,
                      border: '1px solid #bfdbfe'
                    }}>
                      👤 {st.full_name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedActivity.photos && selectedActivity.photos.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#334155' }}>
                  Photos ({selectedActivity.photos.length})
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
                  {selectedActivity.photos.map((photoUrl, index) => (
                    <div key={index} style={{ aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      <img src={photoUrl} alt="Photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 8 }}>
              {selectedActivity.status !== 'approved' && (
                <button
                  type="button"
                  className="primary-button"
                  style={{ background: '#10b981', borderColor: '#10b981' }}
                  onClick={() => updateStatus(selectedActivity.id || selectedActivity.activity_id, 'approved')}
                >
                  Approve
                </button>
              )}
              {selectedActivity.status !== 'rejected' && (
                <button
                  type="button"
                  className="primary-button"
                  style={{ background: '#ef4444', borderColor: '#ef4444' }}
                  onClick={() => updateStatus(selectedActivity.id || selectedActivity.activity_id, 'rejected')}
                >
                  Reject
                </button>
              )}
              <button
                type="button"
                className="secondary-button"
                onClick={() => setSelectedActivity(null)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
